import { useState, useRef, useEffect } from 'react';
import { Bell, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../lib/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  addDoc, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../lib/firestoreErrorHandler';

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  createdAt: any;
  read: boolean;
  type: 'success' | 'info' | 'warning';
  link?: string;
}

export const createNotification = async (notif: Omit<Notification, 'id' | 'createdAt' | 'read'>) => {
  if (!notif.userId) {
    console.warn("createNotification: skipping as userId is undefined", notif);
    return;
  }
  // senderId = auteur réel (imposé par les règles Firestore) : traçabilité
  // anti-spam. Sans utilisateur connecté, on n'écrit pas.
  const senderId = auth.currentUser?.uid;
  if (!senderId) {
    console.warn("createNotification: skipping, no authenticated user");
    return;
  }

  try {
    await addDoc(collection(db, 'notifications'), {
      ...notif,
      senderId,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    handleFirestoreError(err, OperationType.CREATE, 'notifications');
  }
};

export function NotificationBell({ role }: { role: 'festival' | 'stand' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];
      setNotifications(notifs);
    }, (error) => {
      console.error("Error fetching notifications:", error);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllAsRead = async () => {
    if (!user) return;
    const batch = writeBatch(db);
    notifications.filter(n => !n.read).forEach(n => {
      const docRef = doc(db, 'notifications', n.id);
      batch.update(docRef, { read: true });
    });
    try {
      await batch.commit();
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      const docRef = doc(db, 'notifications', id);
      await updateDoc(docRef, { read: true });
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '...';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffInSecs = Math.floor((now.getTime() - date.getTime()) / 1000);
      
      if (diffInSecs < 60) return 'À l\'instant';
      if (diffInSecs < 3600) return `Il y a ${Math.floor(diffInSecs / 60)} min`;
      if (diffInSecs < 86400) return `Il y a ${Math.floor(diffInSecs / 3600)} h`;
      return date.toLocaleDateString();
    } catch (err) {
      return '';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'text-green-500 bg-green-500/10';
      case 'warning': return 'text-yellow-500 bg-yellow-500/10';
      default: return 'text-[#4169E1] bg-[#4169E1]/10';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
      >
        <Bell className="w-5 h-5 text-gray-400 hover:text-white transition-colors" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-[#0A0A0A]"></span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-[#1A1A1A] border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden text-left origin-top-right">
          <div className="p-4 border-b border-white/5 flex justify-between items-center bg-[#121212]">
            <h3 className="font-bold text-white">Notifications {unreadCount > 0 && <span className="bg-[#FF5C00] text-white text-xs px-2 py-0.5 rounded-full ml-2">{unreadCount}</span>}</h3>
            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="text-xs text-gray-400 hover:text-white transition-colors">
                Tout marquer comme lu
              </button>
            )}
          </div>
          
          <div className="max-h-[400px] overflow-y-auto w-full">
            {notifications.length > 0 ? (
              <div className="divide-y divide-white/5">
                {notifications.map((notif) => (
                  <div 
                    key={notif.id} 
                    className={`p-4 hover:bg-white/5 transition-colors cursor-pointer block ${!notif.read ? 'bg-white/[0.02]' : ''}`}
                    onClick={() => {
                      markAsRead(notif.id);
                      if (notif.link) navigate(notif.link);
                      setIsOpen(false);
                    }}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${getTypeColor(notif.type)}`}>
                        {notif.type === 'success' ? <Check className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1">
                          <p className={`text-sm font-bold ${!notif.read ? 'text-white' : 'text-gray-300'}`}>{notif.title}</p>
                          {!notif.read && <span className="w-2 h-2 bg-[#FF5C00] rounded-full shrink-0 mt-1.5 ml-2"></span>}
                        </div>
                        <p className={`text-xs mb-1.5 line-clamp-2 ${!notif.read ? 'text-gray-300' : 'text-gray-500'}`}>
                          {notif.message}
                        </p>
                        <p className="text-[10px] text-gray-600 font-semibold">{formatTime(notif.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 text-sm">
                Aucune notification
              </div>
            )}
          </div>
          
          {notifications.length > 0 && (
            <div className="p-3 text-center border-t border-white/5 bg-[#121212]">
              <button 
                onClick={() => { navigate('/notifications'); setIsOpen(false); }}
                className="text-sm font-semibold text-[#FF5C00] hover:text-white transition-colors"
              >
                Voir toutes les notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
